import IconButton from "@material-ui/core/IconButton"
import LinearProgress from "@material-ui/core/LinearProgress"
import makeStyles from "@material-ui/core/styles/makeStyles"
import Tabs from "@material-ui/core/Tabs"
import Tab from "@material-ui/core/Tab"
import DeleteIcon from "@material-ui/icons/Delete"
import Grid from "@material-ui/core/Grid"
import CloudDownload from "@material-ui/icons/CloudDownload"
import AutorenewIcon from '@material-ui/icons/Autorenew'
import Visibility from "@material-ui/icons/Visibility"
import Typography from "@material-ui/core/Typography"
import Paper from "@material-ui/core/Paper"
import Dialog from "@material-ui/core/Dialog"
import DialogActions from "@material-ui/core/DialogActions"
import DialogContent from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import Button from "@material-ui/core/Button"
import React, { Fragment, useEffect, useState } from "react"
import { useDownload, useFilesManage, useSelectFiles, useUpload } from "./hooks/upload"
import InputAdornment from "@material-ui/core/InputAdornment"
import CancelIcon from "@material-ui/icons/Cancel"
import TextField from "@material-ui/core/TextField"
import { throttle } from "Methods/functions"
import request from "Methods/request"

const useStyles = makeStyles({
    root: {
        width: "100%",
        marginTop: 12,
        marginBottom: 12,
        position: "relative",
    },
    paper: {
        cursor: "pointer",
        width: "100%",
        height: 200,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "10px 0",
        overflowY: "scroll",
    },
    image: {
        borderRadius: 10,
        width: 400,
        height: 150,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
    },
    mask: {
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        borderRadius: "10px",
        position: "absolute",
        background: "rgba(0,0,0,.8)",
        overflow: "hidden",
    },
    input: {
        width: 500,
    },
})

interface ComponentProps {
    initialHeadImg: {
        name: string,
        url: string,
    },
    required?: boolean,
    disabled?: boolean,
    fieldName: string,
    onConfirm: (fieldName: string, headImg: {
        name: string,
        url: string,
    }) => void,
}

const HeadImg = (props: ComponentProps) => {
    const classes = useStyles()
    const { initialHeadImg, fieldName, onConfirm, required, disabled = false } = props
    const [ headImg, setHeadImg ] = useState(initialHeadImg)
    useEffect(() => {
        setHeadImg(initialHeadImg)
    }, [initialHeadImg])
    const {
        files,
        handleSelectFiles,
        handleChangeFile,
        handleResetFile,
        handleRemoveFile,
    } = useSelectFiles(false, "image/*")
    const { upload } = useUpload()
    const { uploading } = useFilesManage(files, upload, handleChangeFile, handleRemoveFile)
    const { handleDownload } = useDownload()
    useEffect(() => {
        if (files[0] && files[0].url) {
            setHeadImg({
                url: files[0].url,
                name: files[0].name,
            })
            handleResetFile()
            onConfirm(fieldName, {
                url: files[0].url,
                name: files[0].name,
            })
        }
        // eslint-disable-next-line
    }, [files])
    const chooseImage = (url: string, name: string) => {
        setHeadImg({ url, name })
        onConfirm(fieldName, { url, name })
    }

    const handleCoverUrl = throttle(async() => {
        if(headImg.url && headImg.url.indexOf('i-young.guokr.net') < 0) {
            const {data} =  await request.post("/apis/covert_qiniu_url", {
                url: headImg.url
            })
            setHeadImg({
                url: data.qiniu_url,
                name:`网络转码.${data.file_type.split('/').pop()}`,
            })
        }
    }, 1000)

    return (
        <div className={classes.root}>
            <Grid container justify="space-between" alignItems="center">
                <Typography color="textSecondary">
                    {`题图${required ? "*" : ""}${disabled ? "" : "（点击图片可再次上传）"}`}
                </Typography>
                <div>
                    <IconButton size="small" color="primary">
                        <CloudDownload fontSize="small" onClick={() => {
                            handleDownload(headImg.url, headImg.name)
                        }}/>
                    </IconButton>
                    <IconButton size="small" color="primary" onClick={() => {
                        window.open(headImg.url, "_blank")
                    }}>
                        <Visibility fontSize="small" />
                    </IconButton>
                    {
                        disabled ? null : (
                            <IconButton size="small" color="secondary" onClick={() => {
                                setHeadImg({
                                    url: "",
                                    name: "",
                                })
                                onConfirm(fieldName, {
                                    url: "",
                                    name: "",
                                })
                            }}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )
                    }
                </div>
            </Grid>
            <Grid container justify="space-between" alignItems="center">
                <Typography color="textSecondary">
                    (外网图片预览失败，点击转换为内网图片预览)
                </Typography>
                <div>
                    <IconButton size="small" color="primary">
                        <AutorenewIcon fontSize="small" onClick={() => {
                            handleCoverUrl()
                        }}/>
                    </IconButton>
                </div>
            </Grid>
            <div className={classes.root} >
                {
                    disabled ? (
                        <Paper className={classes.paper}>
                            {!headImg.url ? (
                                <Typography variant="body2" color="textPrimary">
                                    暂未上传
                                </Typography>
                            ) : (
                                <div className={classes.image} style={{ backgroundImage: `url(${headImg.url})` }} />
                            )}
                        </Paper>
                    ) : (
                        <ImageChooseController
                            chooseImage={chooseImage}
                            headImg={headImg}
                            handleSelectFiles={handleSelectFiles}
                        />
                    )
                }

                {
                    uploading && (
                        <div className={classes.mask}>
                            <LinearProgress variant="query" color="primary" />
                        </div>
                    )
                }
            </div>
        </div>
    )
}
const isUrl = (url: string) => {
    return /^https?:\/\/.+/.test(url)
}
const ImageChooseController = (props: any) => {
    const { headImg, handleSelectFiles, chooseImage } = props
    const classes = useStyles()
    const [ index, setIndex ] = useState(0)
    const [ open, setOpen ] = useState(false)
    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setIndex(newValue)
    }
    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }
    const onClick = () => {
        if (index === 0) {
            return handleSelectFiles()
        } else {
            return handleClickOpen()
        }
    }
    useEffect(() => {
        const initialUrl =
            headImg.url &&headImg.url.indexOf("http") === -1
                ? `${window.location.protocol}${headImg.url}`
                : headImg.url
        setUrl(initialUrl)
    }, [headImg])
    const initialUrl = headImg.url &&headImg.url.indexOf("http") === -1
        ? `${window.location.protocol}${headImg.url}`
        : headImg.url
    const [ url, setUrl ] = useState(initialUrl)
    const [ error, setError ] = useState(false)
    const handleChangeUrl = (event: any) => {
        const editUrl = event.target.value
        setUrl(editUrl)
        if (!isUrl(editUrl)) {
            setError(true)
        } else {
            setError(false)
        }
    }
    const handleClearUrl = () => {
        setUrl("")
        setError(false)
    }
    const textFieldCommonProps = {
        variant: "outlined" as "outlined",
        margin: "normal" as "normal",
        fullWidth: true,
        spellCheck: false,
    }
    const handleSubmit = () => {
        if (error || url === "") {
            if (url === "") {
                setError(true)
            }
            return
        } else {
            chooseImage(url, "网络图片")
        }
        handleClose()
    }
    const handleEnterSubmit = async (event: React.KeyboardEvent) => {
        if (event.keyCode === 13) {
            await handleSubmit()
        }
    }
    const handleCancel = () => {
        handleClose()
        setTimeout(() => {
            setUrl(initialUrl)
            setError(false)
        }, 0)
    }
    return <Fragment>
        <Tabs value={index} onChange={handleChange}>
            <Tab label="本地图片" value={0} />
            <Tab label="网络图片" value={1} />
        </Tabs>
        {
            headImg.url ? (
                <Paper className={classes.paper} onClick={onClick}>
                    <div className={classes.image} style={{ backgroundImage: `url(${headImg.url})` }} />
                </Paper>
            ) : (
                <Paper className={classes.paper} onClick={onClick}>
                    <Typography variant="body2" color="textPrimary">
                        点击上传
                    </Typography>
                </Paper>
            )
        }
        <Dialog
            open={open}
            keepMounted
            onClose={handleClose}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogTitle id="alert-dialog-slide-title">添加网络图片</DialogTitle>
            <DialogContent>
                <TextField
                    className={classes.input}
                    value={url}
                    onChange={handleChangeUrl}
                    onKeyUp={handleEnterSubmit}
                    error={error}
                    helperText={"请输入http或https开头的链接"}
                    label="图片地址"
                    {...textFieldCommonProps}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton tabIndex={-1} onClick={handleClearUrl}>
                                    <CancelIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} color="primary">取消</Button>
                <Button onClick={handleSubmit} color="primary">确定</Button>
            </DialogActions>
        </Dialog>
    </Fragment>
}
export default HeadImg
